import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';

import { editarSucursal, obtenerSucursal, limpiarSucursal } from '../../actions/sucursales';
import { buscarEventos } from '../../actions/eventos';
import { editarBreadcrumb } from '../../actions/breadcrumb';
import { CustomValueContainer } from '../../components/react_select/ReactSelect';

const styles_select = {
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    container: (provided, state) => ({
        ...provided,
    }),
    control: (provided, state) => ({
        ...provided,
        paddingTop: '5px',
        paddingBottom: '5px',
    }),
    valueContainer: (provided, state) => ({
        ...provided,
        overflow: "visible",
        marginTop: "10px"
    }),
    placeholder: (provided, state) => ({
        ...provided,
        position: "absolute",
        top: state.hasValue || state.selectProps.inputValue ? -15 : "0%",
        transition: "top 0.1s, font-size 0.1s",
        fontSize: (state.hasValue || state.selectProps.inputValue) && 13
    })
};

const tiposSucursal = [
    { value: 0, label: 'Fija' },
    { value: 1, label: 'Móvil' }
];

const EditarSucursal = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const detalles = useSelector(state => state.sucursales.detalles);
    const { listado: eventosListado } = useSelector(state => state.eventos);

    const [sucursalData, setSucursalData] = useState({
        id: null,
        nombre: null,
        direccion: null,
        tipo: null,
        evento: null,
        puntos: []
    });

    const [tipoData, setTipoData] = useState(null);
    const [eventoData, setEventoData] = useState(null);
    const [statusProcesar, setStatusProcesar] = useState(false);

    useEffect(() => {
        return () => {
            dispatch(limpiarSucursal());
        };
    }, []);

    useEffect(() => {
        if (id) {
            dispatch(obtenerSucursal(id));
            dispatch(buscarEventos(true));
            dispatch(editarBreadcrumb([
                { nombre: 'Sucursales', path: '/sucursales', activo: false },
                { nombre: 'Editar sucursal', path: `/sucursales/editar/${id}`, activo: true }
            ]));
        }
    }, [id]);

    useEffect(() => {
        if (detalles != null) {
            setSucursalData({
                id: detalles._id,
                nombre: detalles.nombre ?? null,
                direccion: detalles.direccion ?? null,
                tipo: detalles.tipo ?? null,
                evento: detalles.evento?._id ?? null,
                puntos: detalles.puntos ? [...detalles.puntos] : []
            });

            if (detalles.tipo != null) {
                const tipoEncontrado = tiposSucursal.find(t => t.value === detalles.tipo);
                setTipoData(tipoEncontrado || null);
            }

            setEventoData(detalles.evento ? { value: detalles.evento._id, label: detalles.evento.nombre } : null);
        }
    }, [detalles]);

    const handleInputChange = ({ target }) => {
        setSucursalData({ ...sucursalData, [target.name]: target.value });
    };

    const handleSelectTipo = (option) => {
        setTipoData(option);
        setSucursalData({ ...sucursalData, tipo: option?.value ?? null });
    };

    const handleSelectEvento = (option) => {
        setEventoData(option);
        setSucursalData({ ...sucursalData, evento: option?.value ?? null });
    };

    /* Puntos de revisión */
    const handleAgregarPunto = () => {
        setSucursalData({ ...sucursalData, puntos: [...sucursalData.puntos, { nombre: null }] });
    };

    const handlePuntoChange = (index, value) => {
        const nuevos = [...sucursalData.puntos];
        nuevos[index].nombre = value;
        setSucursalData({ ...sucursalData, puntos: nuevos });
    };

    const handleEliminarPunto = (index) => {
        setSucursalData({ ...sucursalData, puntos: sucursalData.puntos.filter((_, i) => i !== index) });
    };

    const handleSubmit = async () => {
        setStatusProcesar(true);
        await dispatch(editarSucursal(sucursalData));
        setStatusProcesar(false);
        navigate('/sucursales');
    };

    return (
        <div className="row mt-5">
            <div className="col-12 mb-3 d-flex justify-content-between align-items-center">
                <h4 className="mb-4">Editar sucursal</h4>
            </div>

            <div className="col-12">
                <div className="accordion" id="accordionEditarSucursal">

                    {/* Sección 1: Información general */}
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="headingInfo">
                            <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseInfo" aria-expanded="true" aria-controls="collapseInfo">
                                Información general
                            </button>
                        </h2>
                        <div id="collapseInfo" className="accordion-collapse collapse show" aria-labelledby="headingInfo" data-bs-parent="#accordionEditarSucursal">
                            <div className="accordion-body">
                                <div className="row">
                                    <div className="col-md-3 mb-3">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Nombre"
                                                id="nombre"
                                                name="nombre"
                                                value={sucursalData.nombre || ''}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor="nombre">Nombre</label>
                                        </div>
                                    </div>
                                    <div className="col-md-2 form-group mb-3">
                                        <div className="input-group h-100">
                                            <Select
                                                name="tipo"
                                                className="select-group"
                                                classNamePrefix="react-select"
                                                placeholder="Tipo"
                                                options={tiposSucursal}
                                                value={tipoData}
                                                onChange={handleSelectTipo}
                                                menuPortalTarget={document.body}
                                                components={{ ValueContainer: CustomValueContainer }}
                                                styles={{ ...styles_select }}
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-3 form-group mb-3">
                                        <div className="input-group h-100">
                                            <Select
                                                name="evento"
                                                className="select-group"
                                                classNamePrefix="react-select"
                                                placeholder="Evento"
                                                options={(eventosListado || []).map(e => ({ value: e._id, label: e.nombre }))}
                                                value={eventoData}
                                                onChange={handleSelectEvento}
                                                menuPortalTarget={document.body}
                                                components={{ ValueContainer: CustomValueContainer }}
                                                styles={{ ...styles_select }}
                                                isClearable
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <div className="form-floating">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Dirección"
                                                id="direccion"
                                                name="direccion"
                                                value={sucursalData.direccion || ''}
                                                onChange={handleInputChange}
                                            />
                                            <label htmlFor="direccion">Dirección</label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Sección 2: Puntos de revisión */}
                    <div className="accordion-item">
                        <h2 className="accordion-header" id="headingPuntos">
                            <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapsePuntos" aria-expanded="false" aria-controls="collapsePuntos">
                                Puntos de revisión
                            </button>
                        </h2>
                        <div id="collapsePuntos" className="accordion-collapse collapse" aria-labelledby="headingPuntos" data-bs-parent="#accordionEditarSucursal">
                            <div className="accordion-body">
                                {sucursalData.puntos.map((punto, index) => (
                                    <div className="row align-items-center mb-2" key={index}>
                                        <div className="col">
                                            <div className="form-floating">
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="Nombre del punto"
                                                    id={`punto-nombre-${index}`}
                                                    value={punto.nombre || ''}
                                                    onChange={(e) => handlePuntoChange(index, e.target.value)}
                                                />
                                                <label htmlFor={`punto-nombre-${index}`}>Nombre del punto</label>
                                            </div>
                                        </div>
                                        <div className="col-auto">
                                            <button
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleEliminarPunto(index)}
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                                <button className="btn btn-outline-primary btn-sm mt-2" onClick={handleAgregarPunto}>
                                    <i className="fas fa-plus me-1"></i> Agregar punto
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            <div className="col-12 mt-3 d-flex justify-content-end">
                <button className="btn btn-secondary me-2" onClick={() => navigate('/sucursales')} disabled={statusProcesar}>
                    Cancelar
                </button>
                {
                    statusProcesar
                        ? <button className="btn btn-success" disabled><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span> Guardando</button>
                        : <button className="btn btn-success" onClick={handleSubmit}><i className="fas fa-check me-1"></i> Guardar</button>
                }
            </div>
        </div>
    );
};

export default EditarSucursal;
